import React, { useState, useCallback } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Progress,
  Tab,
  Tabs,
} from "@nextui-org/react";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";
import axios from "axios";

const UploadTaskMedia = ({ isOpen, onClose, taskId, onUploadComplete }) => {
  const [activeTab, setActiveTab] = useState("images");
  const [imageFiles, setImageFiles] = useState([]);
  const [documentFiles, setDocumentFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const authToken = localStorage.getItem("token");

  // Handle image drop
  const onImageDrop = useCallback((acceptedFiles) => {
    const validFiles = acceptedFiles.filter(file => 
      file.type.startsWith('image/')
    );
    
    if (validFiles.length < acceptedFiles.length) {
      toast.warning("Only image files are accepted in the Images tab", {
        position: "bottom-right",
        autoClose: 3000,
      });
    }
    
    setImageFiles(prev => [...prev, ...validFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      id: Math.random().toString(36).substr(2, 9),
      type: 'image'
    }))]);
  }, []);

  // Handle document drop
  const onDocumentDrop = useCallback((acceptedFiles) => {
    const validFiles = acceptedFiles.filter(file => 
      file.type === 'application/pdf' ||
      file.type === 'application/msword' ||
      file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      file.type === 'application/vnd.ms-excel' ||
      file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.type === 'text/plain' ||
      file.type === 'application/rtf'
    );
    
    if (validFiles.length < acceptedFiles.length) {
      toast.warning("Only document files (PDF, DOC, DOCX, XLS, XLSX, TXT, RTF) are accepted", {
        position: "bottom-right",
        autoClose: 3000,
      });
    }
    
    setDocumentFiles(prev => [...prev, ...validFiles.map(file => ({
      file,
      preview: null,
      id: Math.random().toString(36).substr(2, 9),
      type: 'document',
      icon: getDocumentIcon(file.type)
    }))]);
  }, []);

  // Get document icon based on file type
  const getDocumentIcon = (fileType) => {
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('word') || fileType.includes('document')) return '📝';
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return '📊';
    if (fileType.includes('text')) return '📃';
    return '📁';
  };

  const imageDropzone = useDropzone({
    onDrop: onImageDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.svg']
    },
    maxSize: 50 * 1024 * 1024, // 50MB
    disabled: uploading
  });

  const documentDropzone = useDropzone({
    onDrop: onDocumentDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/plain': ['.txt'],
      'application/rtf': ['.rtf']
    },
    maxSize: 50 * 1024 * 1024, // 50MB
    disabled: uploading
  });

  const removeFile = (fileId, type) => {
    if (type === 'image') {
      setImageFiles(prev => {
        const fileToRemove = prev.find(f => f.id === fileId);
        if (fileToRemove?.preview) {
          URL.revokeObjectURL(fileToRemove.preview);
        }
        return prev.filter(f => f.id !== fileId);
      });
    } else {
      setDocumentFiles(prev => prev.filter(f => f.id !== fileId));
    }
  };

  const uploadFiles = async () => {
    const hasImages = imageFiles.length > 0;
    const hasDocuments = documentFiles.length > 0;

    if (!hasImages && !hasDocuments) {
      toast.error("Please select files to upload", {
        position: "bottom-right",
        autoClose: 1500,
      });
      return;
    }

    setUploading(true);
    let successCount = 0;
    let failCount = 0;

    // Upload images if any
    if (hasImages) {
      try {
        const imageFormData = new FormData();
        imageFiles.forEach((fileData) => {
          imageFormData.append('images[]', fileData.file);
        });

        console.log('Uploading images to:', `/tasks/${taskId}/images`);
        
        await axios.post(
          `/tasks/${taskId}/images`,
          imageFormData,
          {
            headers: {
              'Authorization': `Bearer ${authToken}`,
              'Accept': 'application/json',
            },
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(percentCompleted / (hasDocuments ? 2 : 1));
            },
          }
        );
        
        successCount += imageFiles.length;
        
        // Clean up preview URLs
        imageFiles.forEach(file => {
          if (file.preview) {
            URL.revokeObjectURL(file.preview);
          }
        });
        
      } catch (error) {
        console.error('Image upload error:', error);
        failCount += imageFiles.length;
        toast.error("Failed to upload some images", {
          position: "bottom-right",
          autoClose: 2000,
        });
      }
    }

    // Upload documents if any
    if (hasDocuments) {
      try {
        const documentFormData = new FormData();
        documentFiles.forEach((fileData) => {
          documentFormData.append('documents[]', fileData.file);
        });

        console.log('Uploading documents to:', `/tasks/${taskId}/documents`);
        
        await axios.post(
          `/tasks/${taskId}/documents`,
          documentFormData,
          {
            headers: {
              'Authorization': `Bearer ${authToken}`,
              'Accept': 'application/json',
            },
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(50 + (percentCompleted / 2));
            },
          }
        );
        
        successCount += documentFiles.length;
        
      } catch (error) {
        console.error('Document upload error:', error);
        failCount += documentFiles.length;
        toast.error("Failed to upload some documents", {
          position: "bottom-right",
          autoClose: 2000,
        });
      }
    }

    setUploadProgress(100);

    // Show summary toast
    if (successCount > 0) {
      toast.success(`Successfully uploaded ${successCount} file${successCount > 1 ? 's' : ''}`, {
        position: "bottom-right",
        autoClose: 1500,
      });
      
      if (onUploadComplete) {
        onUploadComplete();
      }
    }

    if (failCount > 0) {
      toast.error(`Failed to upload ${failCount} file${failCount > 1 ? 's' : ''}`, {
        position: "bottom-right",
        autoClose: 1500,
      });
    }

    // Reset and close
    setTimeout(() => {
      setImageFiles([]);
      setDocumentFiles([]);
      setUploadProgress(0);
      setUploading(false);
      onClose();
    }, 1500);
  };

  const handleClose = () => {
    // Clean up all preview URLs
    imageFiles.forEach(file => {
      if (file.preview) {
        URL.revokeObjectURL(file.preview);
      }
    });
    setImageFiles([]);
    setDocumentFiles([]);
    setUploadProgress(0);
    setUploading(false);
    onClose();
  };

  // SVG Icon components
  const PhotoIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-12 h-12 mx-auto text-gray-400 mb-3">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
    </svg>
  );

  const DocumentIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-12 h-12 mx-auto text-gray-400 mb-3">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  );

  const XMarkIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );

  const totalFiles = imageFiles.length + documentFiles.length;

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleClose}
      size="2xl"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          Upload Media
          {totalFiles > 0 && (
            <span className="text-sm font-normal text-gray-500">
              {totalFiles} file{totalFiles > 1 ? 's' : ''} selected
            </span>
          )}
        </ModalHeader>
        <ModalBody>
          <Tabs 
            aria-label="Upload options" 
            selectedKey={activeTab}
            onSelectionChange={setActiveTab}
            disabled={uploading}
          >
            <Tab key="images" title="Images">
              <div
                {...imageDropzone.getRootProps()}
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors mt-4
                  ${imageDropzone.isDragActive ? 'border-mainBlue bg-blue-50' : 'border-gray-300 hover:border-mainBlue'}`}
              >
                <input {...imageDropzone.getInputProps()} />
                <PhotoIcon />
                {imageDropzone.isDragActive ? (
                  <p className="text-mainBlue">Drop the images here...</p>
                ) : (
                  <div>
                    <p className="text-gray-600 mb-2">Drag & drop images here, or click to select</p>
                    <p className="text-sm text-gray-400">Supports: JPG, PNG, GIF, WEBP, BMP, SVG (Max 50MB each)</p>
                  </div>
                )}
              </div>

              {/* Image Files List */}
              {imageFiles.length > 0 && (
                <div className="mt-4 space-y-3">
                  <h4 className="font-medium">Selected Images ({imageFiles.length})</h4>
                  {imageFiles.map((file) => (
                    <div
                      key={file.id}
                      className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg"
                    >
                      {file.preview && (
                        <img
                          src={file.preview}
                          alt={file.file.name}
                          className="w-12 h-12 object-cover rounded"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{file.file.name}</p>
                        <p className="text-xs text-gray-500">
                          {(file.file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                      <button
                        onClick={() => removeFile(file.id, 'image')}
                        disabled={uploading}
                        className="p-1 hover:bg-gray-200 rounded-full disabled:opacity-50"
                      >
                        <XMarkIcon />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </Tab>

            <Tab key="documents" title="Documents">
              <div
                {...documentDropzone.getRootProps()}
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors mt-4
                  ${documentDropzone.isDragActive ? 'border-mainBlue bg-blue-50' : 'border-gray-300 hover:border-mainBlue'}`}
              >
                <input {...documentDropzone.getInputProps()} />
                <DocumentIcon />
                {documentDropzone.isDragActive ? (
                  <p className="text-mainBlue">Drop the documents here...</p>
                ) : (
                  <div>
                    <p className="text-gray-600 mb-2">Drag & drop documents here, or click to select</p>
                    <p className="text-sm text-gray-400">Supports: PDF, DOC, DOCX, XLS, XLSX, TXT, RTF (Max 50MB each)</p>
                  </div>
                )}
              </div>

              {/* Document Files List */}
              {documentFiles.length > 0 && (
                <div className="mt-4 space-y-3">
                  <h4 className="font-medium">Selected Documents ({documentFiles.length})</h4>
                  {documentFiles.map((file) => (
                    <div
                      key={file.id}
                      className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg"
                    >
                      <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center text-2xl">
                        {getDocumentIcon(file.file.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{file.file.name}</p>
                        <p className="text-xs text-gray-500">
                          {(file.file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                      <button
                        onClick={() => removeFile(file.id, 'document')}
                        disabled={uploading}
                        className="p-1 hover:bg-gray-200 rounded-full disabled:opacity-50"
                      >
                        <XMarkIcon />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </Tab>
          </Tabs>

          {/* Upload Progress */}
          {uploading && (
            <div className="mt-4">
              <Progress
                value={uploadProgress}
                className="max-w-md"
                color="primary"
                showValueLabel={true}
              />
              <p className="text-sm text-gray-500 mt-2 text-center">
                Uploading... {uploadProgress}%
              </p>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="danger" variant="light" onPress={handleClose} disabled={uploading}>
            Cancel
          </Button>
          <Button 
            color="primary" 
            onPress={uploadFiles}
            disabled={totalFiles === 0 || uploading}
            isLoading={uploading}
          >
            {uploading ? 'Uploading...' : `Upload ${totalFiles} File${totalFiles !== 1 ? 's' : ''}`}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default UploadTaskMedia;